import { Compass, Users, Bell, Sparkles } from "lucide-react";
import Reveal from "./Reveal";

const DISCOVERY_IMG =
  "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=1600&auto=format&fit=crop";
const TICKET_IMG =
  "https://images.unsplash.com/photo-1603910234616-3b5f4a6be2b4?q=80&w=1200&auto=format&fit=crop";

const cardCls =
  "group relative flex flex-col overflow-hidden rounded-3xl border border-brand-purple/20 bg-brand-card/80 backdrop-blur-xl transition-all duration-500 hover:border-brand-purple/60 hover:shadow-[0_0_60px_rgba(140,82,255,0.25)]";

const ImgLayer = ({ src, alt }) => (
  <>
    <img
      src={src}
      alt={alt}
      className="absolute inset-0 h-full w-full object-cover opacity-45 saturate-[0.85] transition-transform duration-[1.2s] ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0612] via-[#1a0640]/40 to-brand-purple/10" />
  </>
);

const Tag = ({ children }) => (
  <span className="w-max rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">
    {children}
  </span>
);

export default function Bento() {
  return (
    <section id="discovery" className="relative py-24 lg:py-32">
      <div
        className="pointer-events-none absolute right-[-12%] top-24 h-[520px] w-[520px] rounded-full bg-[#2b0086] opacity-30 blur-[170px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Tag>The app</Tag>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            One layer for the <span className="text-gradient">whole night out.</span>
          </h2>
          <p className="mt-4 max-w-xl text-brand-mist/65">
            Discovery, your crew and the whole scene in a single mobile-native flow, designed
            for engagement and built for flow.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-4 auto-rows-fr md:grid-cols-3 lg:gap-6" data-testid="bento-grid">
          <Reveal className="md:col-span-2" delay={0}>
            <article className={`${cardCls} h-full min-h-[340px] p-7 lg:p-9`} data-testid="bento-card-discovery">
              <ImgLayer src={DISCOVERY_IMG} alt="Festival crowd under neon lights" />
              <div className="relative z-10 mt-auto">
                <Tag>Discovery</Tag>
                <h3 className="mt-4 font-display text-2xl font-bold text-brand-paper lg:text-3xl">
                  Discovery that feels alive
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-brand-mist/70">
                  A personalized, scroll-driven feed of Boston's live events: trending
                  moments, location-based picks, artist and venue info, all tailored to your
                  behavior and social circles.
                </p>
              </div>
            </article>
          </Reveal>

          <Reveal className="md:row-span-2" delay={0.08}>
            <article className={`${cardCls} h-full min-h-[420px] p-7`} data-testid="bento-card-events">
              <ImgLayer src={TICKET_IMG} alt="Concert crowd under purple confetti" />
              <div className="relative z-10 mt-auto">
                <Tag>Event pages</Tag>
                <h3 className="mt-4 font-display text-2xl font-bold text-brand-paper">
                  Every event, in depth
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-mist/70">
                  Trending events, location-based discovery and artist &amp; venue info.
                  Everything you need to know before you go.
                </p>
              </div>
            </article>
          </Reveal>

          <Reveal delay={0.12}>
            <article className={`${cardCls} h-full min-h-[220px] justify-end p-7`} data-testid="bento-card-social">
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-brand-teal/30 bg-brand-teal/10 text-brand-teal">
                <Users className="h-5 w-5" />
              </span>
              <Tag>Social</Tag>
              <h3 className="mt-3 font-display text-xl font-bold text-brand-paper">Built social</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-mist/70">
                Friend activity, group attendance and event sharing. Coordinate who you go with,
                not just that you go.
              </p>
            </article>
          </Reveal>

          <Reveal delay={0.18}>
            <article className={`${cardCls} h-full min-h-[220px] justify-end p-7`} data-testid="bento-card-alerts">
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-brand-gold/30 bg-brand-gold/10 text-brand-gold">
                <Bell className="h-5 w-5" />
              </span>
              <Tag>Smart alerts</Tag>
              <h3 className="mt-3 font-display text-xl font-bold text-brand-paper">
                Never miss a moment
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-mist/70">
                Smart alerts for the events you care about: new listings, lineup drops and
                last-minute updates, delivered the moment they happen.
              </p>
            </article>
          </Reveal>

          <Reveal className="md:col-span-3" delay={0.1}>
            <article
              className={`${cardCls} h-full flex-row items-center gap-8 p-7 lg:p-9`}
              data-testid="bento-card-recs"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-brand-purple/40 bg-brand-purple/15 text-brand-purple">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <Tag>Personalized</Tag>
                <h3 className="mt-3 font-display text-xl font-bold text-brand-paper lg:text-2xl">
                  Recommendations that learn you
                </h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-brand-mist/70">
                  Smart personalization models your taste, so the more you scroll and go, the
                  closer the feed gets to your scene.
                </p>
              </div>
              <div className="ml-auto hidden flex-wrap justify-end gap-2 xl:flex">
                {["Live music", "Nightlife", "Comedy", "Pop-ups", "Sports"].map((c) => (
                  <span
                    key={c}
                    className="rounded-full border border-brand-mist/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-brand-mist/75"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
